import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AddPhotoImageData } from "../../../types";
import { editReview, editReviewDesktop } from "../../../apis";
import { Storage } from "common/storage";
import { applyReviewEditToVenueCache } from "../details/venueReviewsCache";

/**
 * DEV-544/545 — edits an existing free-text experience (notes, Sweet/Sour rating and
 * newly added photos). Counterpart to {@link usePostVenueReview}; photo *removals* go
 * through `useDeletePhotoMutation` before this runs.
 *
 * `images` carries only newly attached photos — passing the review's existing ones
 * would re-upload them as duplicates.
 */
export function useEditVenueReview(isDesktop?: boolean) {
  const accessToken = Storage.getCurrentUserToken();
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, error, status, data, reset } = useMutation({
    mutationFn: ({
      reviewId,
      review,
      images,
      accessibilityExperience,
    }: {
      images: AddPhotoImageData[];
      reviewId: string;
      review: string;
      accessibilityExperience: string;
    }) => {
      return isDesktop
        ? editReviewDesktop(
            reviewId,
            review,
            images,
            accessibilityExperience,
            accessToken || ""
          )
        : editReview(
            reviewId,
            review,
            images,
            accessibilityExperience,
            accessToken || ""
          );
    },
    onSuccess: (result, { reviewId, review }) => {
      // Patch here rather than leaving it to callers: `ReviewSaved` rebuilds the venue
      // cache asynchronously, so the refetch below still returns the pre-edit list.
      // Every consumer needs this, and one that forgets sees the edit revert a couple
      // of seconds later.
      applyReviewEditToVenueCache(queryClient, {
        reviewId: result.reviewId || Number(reviewId),
        notes: review,
        accessibilityExperience: result.accessibilityExperience,
        reviewPhotos: result.reviewPhotos,
      });

      // Mark stale, but do NOT refetch now. `ReviewSaved` rebuilds the venue cache on
      // a queue with no bounded delay, so any refetch we schedule ourselves is a race
      // we usually lose: it returns the pre-edit list and silently reverts the patch
      // above a second or two after the user saved. The next mount or refocus fetches
      // the server's copy, by which time the rebuild has landed.
      queryClient.invalidateQueries({
        queryKey: ["loadVenueDetailsBySlug"],
        exact: false,
        refetchType: "none",
      });
    },
  });

  return { mutate, mutateAsync, error, status, data, reset };
}
